import FieldNames from "../defaults/FieldNames.json";
import GeneralService from "../services/GeneralService";
import { createQueryObject } from '../utils/QueryHelper';
import { getAppConfig, getSummaryViewRootServiceUrl } from '../utils/ReduxHelper';
import {formatDateForDisplay, sortFeatureAttributesByDate} from "./DateHelper";

export const queryAllSummaryFeatureTables = (definitionExpression, classes, isWithFunding) => {
  return new Promise((resolve, reject) => {
    let tempFeatures = [];
    let outFields = "*";
    let url = "";
    const config = getAppConfig();
    const summaryViewUrl = getSummaryViewRootServiceUrl();

    if(isWithFunding){
      url = `${summaryViewUrl}/${config.summaryFundingSingleLineView[config.env].layerId}`;
      outFields = `${FieldNames.featureFields.id}, ${FieldNames.featureFields.objectID}, 
        ${FieldNames.featureFields.calculatedAcresFundingSingleLineView}, ${FieldNames.fundingFields.totalCost}, ${FieldNames.featureFields.class}, 
        ${FieldNames.featureFields.type}, ${FieldNames.featureFields.subType}, ${FieldNames.featureFields.category}, ${FieldNames.featureFields.isWUI}, ${FieldNames.featureFields.department}, 
        ${FieldNames.featureFields.agency}, ${FieldNames.featureFields.region}, ${FieldNames.featureFields.unit}, ${FieldNames.featureFields.tribeName}, 
        ${FieldNames.featureFields.fundingSource}, ${FieldNames.featureFields.fundingSourceProgram}, ${FieldNames.featureFields.fundingSourceCategory}, 
        ${FieldNames.featureFields.fundingSourceSubCategory}, ${FieldNames.featureFields.fundingDepartment}, ${FieldNames.featureFields.fundingAgency}, 
        ${FieldNames.featureFields.fundingRegion}, ${FieldNames.featureFields.fundingUnit}, ${FieldNames.featureFields.fundingTribe}, ${FieldNames.featureFields.fundingSubUnit}, 
        ${FieldNames.featureFields.treatmentDriver}, ${FieldNames.featureFields.congressionalDistrictNumber}, ${FieldNames.featureFields.state}, ${FieldNames.featureFields.county}, 
        ${FieldNames.featureFields.initiationDate}, ${FieldNames.featureFields.completionDate}`;
    } else {
      url = `${summaryViewUrl}/${config.summaryFundingConcantenatedView[config.env].layerId}`;
      outFields = `${FieldNames.featureFields.id}, ${FieldNames.featureFields.objectID}, 
        ${FieldNames.featureFields.calculatedArea}, ${FieldNames.featureFields.estimatedTotalCost}, ${FieldNames.featureFields.class}, 
        ${FieldNames.featureFields.type}, ${FieldNames.featureFields.subType}, ${FieldNames.featureFields.category}, ${FieldNames.featureFields.isWUI}, ${FieldNames.featureFields.department}, 
        ${FieldNames.featureFields.agency}, ${FieldNames.featureFields.region}, ${FieldNames.featureFields.unit}, ${FieldNames.featureFields.tribeName}, 
        ${FieldNames.featureFields.fundingSource}, ${FieldNames.featureFields.fundingSourceProgram}, ${FieldNames.featureFields.fundingSourceCategory}, 
        ${FieldNames.featureFields.fundingSourceSubCategory}, ${FieldNames.featureFields.fundingDepartment}, ${FieldNames.featureFields.fundingAgency}, 
        ${FieldNames.featureFields.fundingRegion}, ${FieldNames.featureFields.fundingUnit}, ${FieldNames.featureFields.fundingTribe}, ${FieldNames.featureFields.fundingSubUnit}, 
        ${FieldNames.featureFields.treatmentDriver}, ${FieldNames.featureFields.congressionalDistrictNumber}, ${FieldNames.featureFields.state}, ${FieldNames.featureFields.county}, 
        ${FieldNames.featureFields.initiationDate}, ${FieldNames.featureFields.completionDate}`;
    }

    getAllSummaryServiceFeaturesUntilDone(definitionExpression, classes, outFields, url).then(featureResp => {
      //Returns records from a view so the ID field is what all other tables will join on
      if(featureResp && featureResp.length > 0){
        featureResp.forEach((feature) => {
          if(isWithFunding)
          {
            feature[FieldNames.featureFields.calculatedArea] = feature[FieldNames.featureFields.calculatedAcresFundingSingleLineView];
          }
          feature[FieldNames.featureFields.initiationDate] = formatDateForDisplay(feature[FieldNames.featureFields.initiationDate]);
          feature[FieldNames.featureFields.completionDate] = formatDateForDisplay(feature[FieldNames.featureFields.completionDate]);
          tempFeatures.push(feature);
        });

        sortFeatureAttributesByDate(tempFeatures, FieldNames.featureFields.initiationDate, false);
        resolve(tempFeatures);
      }
      else {
        resolve([]);
      }
    }).catch(error => {
      reject(error);
    });
  });
}

export const queryAllSummaryService = (definitionExpression, outFieldStr) => {
  return new Promise((resolve, reject) => {
    let tempFeatures = [];
    let outFields = "*";
    let url = "";
    const config = getAppConfig();
    const summaryViewUrl = getSummaryViewRootServiceUrl();
    
    url = `${summaryViewUrl}/${config.summaryFundingSingleLineView[config.env].layerId}`;
    outFields = outFieldStr;

    getAllSummaryServiceFeaturesUntilDone(definitionExpression, null, outFields, url).then(featureResp => {
      //Returns records from a view so the ID field is what all other tables will join on
      if(featureResp?.length > 0){
        featureResp.forEach((feature) => {
          feature[FieldNames.featureFields.initiationDate] = formatDateForDisplay(feature[FieldNames.featureFields.initiationDate]);
          feature[FieldNames.featureFields.completionDate] = formatDateForDisplay(feature[FieldNames.featureFields.completionDate]);
          tempFeatures.push(feature);
        });

        sortFeatureAttributesByDate(tempFeatures, FieldNames.featureFields.initiationDate, false);
        resolve(tempFeatures);
      }
      else {
        resolve([]);
      }
    }).catch(error => {
      reject(error);
    });
  });
}

// Could be broken out into a service file, but not really necessary until we have more service calls
const getAllSummaryServiceFeaturesUntilDone = async(definitionExpression, classes, outFields, url) => {
  try
  {
    let allFinalResults = [];
    const numberToTakeEachTime = 10000;
    let startIndex = 0;
    let hasMoreRecords = true;
    let totalFeatureRecords = [];
    let finalResults = "";
    let classFilter = classes?.join(" OR ")

    let baseWhere = (definitionExpression && definitionExpression != null) ? 
      (classFilter ? `((${definitionExpression}) AND (${classFilter}))` : `(${definitionExpression})`) : 
      (classFilter ? `(${classFilter})` : `(OBJECTID IS NOT NULL)`);

    let queryObject = createQueryObject(false, baseWhere, numberToTakeEachTime, startIndex, null, outFields);

    while(hasMoreRecords){
      finalResults = await GeneralService.queryCall(url, queryObject);
      if(finalResults && finalResults.length > 0){
        allFinalResults.push(finalResults);
        totalFeatureRecords.push(finalResults);
        if(finalResults.length === numberToTakeEachTime){
          startIndex += numberToTakeEachTime;
          queryObject.start = startIndex;
        } else {
          hasMoreRecords = false;
        }
      }
      else {
        hasMoreRecords = false;
      }
    }

    const allFinalResultsFlattened = (allFinalResults != null && allFinalResults.length > 0) ? allFinalResults.flat() : [];
    return allFinalResultsFlattened;
  }
  catch(e)
  {
    return new Promise((resolve,reject) => {
      reject(e);
    });
  }
}